/**
 * AW UGC Video List — carousel/grid controller, hover preview and lightbox.
 * Native custom element, no external slider library. Layout (grid vs
 * carousel, per breakpoint) is decided entirely by CSS; this controller
 * only drives interactions that exist in the DOM, so it behaves correctly
 * as a no-op wherever the track isn't actually scrollable.
 */
(function () {
  if (customElements.get('aw-ugc-video-list')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var HOVER_CAPABLE = window.matchMedia('(hover: hover) and (pointer: fine)');

  function buildEmbedUrl(kind, id) {
    if (kind === 'youtube') {
      return 'https://www.youtube.com/embed/' + id + '?autoplay=1&mute=0&playsinline=1&rel=0';
    }
    if (kind === 'vimeo') {
      return 'https://player.vimeo.com/video/' + id + '?autoplay=1&muted=0&playsinline=1';
    }
    return '';
  }

  class AwUgcVideoList extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-aw-track]');
      if (!this.track) return;

      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-aw-item]'));
      this.prevBtn = this.querySelector('[data-aw-prev]');
      this.nextBtn = this.querySelector('[data-aw-next]');
      this.dotsWrap = this.querySelector('[data-aw-dots]');
      this.dialog = this.querySelector('[data-aw-dialog]');
      this.dialogInner = this.querySelector('[data-aw-dialog-inner]');
      this.dialogClose = this.querySelector('[data-aw-dialog-close]');
      this.lastTrigger = null;
      this.dragging = false;

      this.boundOnResize = this.onResize.bind(this);
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      window.addEventListener('resize', this.boundOnResize);
      this.track.addEventListener('pointerdown', this.onDragStart.bind(this));
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.addEventListener('touchend', () => this.scheduleAutoplay(), { passive: true });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      if (this.dialogClose) this.dialogClose.addEventListener('click', () => this.closeDialog());
      if (this.dialog) {
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeDialog();
        });
        this.dialog.addEventListener('close', () => this.onDialogClosed());
      }

      this.setupCards();
      this.buildDots();
      this.updateArrows();
      this.setupReveal();
      this.startAutoplay();
    }

    disconnectedCallback() {
      if (this.boundOnResize) window.removeEventListener('resize', this.boundOnResize);
      if (this.revealObserver) this.revealObserver.disconnect();
      this.stopAutoplay();
    }

    setupCards() {
      var hoverPreviewEnabled = this.dataset.hoverPreview === 'true';

      this.items.forEach((item) => {
        var card = item.querySelector('.aw-ugcvl__card');
        var trigger = item.querySelector('[data-aw-open]');
        var preview = item.querySelector('.aw-ugcvl__preview');

        if (preview && hoverPreviewEnabled) {
          item.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'mouse' || !HOVER_CAPABLE.matches) return;
            preview.classList.add('is-ready');
            var playPromise = preview.play();
            if (playPromise && playPromise.catch) playPromise.catch(function () {});
          });
          item.addEventListener('pointerleave', (event) => {
            if (event.pointerType !== 'mouse') return;
            preview.pause();
            try {
              preview.currentTime = 0;
            } catch (err) {}
          });
        }

        if (trigger && card) {
          trigger.addEventListener('click', () => this.openDialog(card, trigger));
        }
      });
    }

    openDialog(card, trigger) {
      if (!this.dialog || !this.dialogInner) return;
      var kind = card.getAttribute('data-aw-kind');
      if (!kind) return;

      this.lastTrigger = trigger;
      this.dialogInner.innerHTML = '';

      if (kind === 'video') {
        var video = document.createElement('video');
        video.src = card.getAttribute('data-aw-src');
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        this.dialogInner.appendChild(video);
      } else {
        var iframe = document.createElement('iframe');
        iframe.src = buildEmbedUrl(kind, card.getAttribute('data-aw-embed-id'));
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        iframe.setAttribute('allowfullscreen', '');
        this.dialogInner.appendChild(iframe);
      }

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    closeDialog() {
      if (this.dialog && typeof this.dialog.close === 'function') {
        this.dialog.close();
      }
    }

    onDialogClosed() {
      if (this.dialogInner) this.dialogInner.innerHTML = '';
      if (this.lastTrigger) this.lastTrigger.focus();
    }

    scrollByPage(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
    }

    startAutoplay() {
      if (this.dataset.autoplay !== 'true' || REDUCED_MOTION.matches || this.autoplayTimer) return;
      var delay = Number(this.dataset.autoplaySpeed) || 4000;
      this.autoplayTimer = window.setInterval(() => this.advanceAutoplay(), delay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      window.clearTimeout(this.autoplayResumeTimer);
      this.autoplayResumeTimer = window.setTimeout(() => this.startAutoplay(), 1200);
    }

    advanceAutoplay() {
      var max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 1) return;
      var atEnd = this.track.scrollLeft >= max - 2;
      this.track.scrollTo({
        left: atEnd ? 0 : Math.min(this.track.scrollLeft + this.track.clientWidth, max),
        behavior: 'smooth'
      });
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.updateArrows();
        this.updateActiveDot();
        this.scrollRaf = null;
      });
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.buildDots();
        this.updateArrows();
      }, 150);
    }

    updateArrows() {
      var max = this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max;
    }

    pageCount() {
      if (!this.track.clientWidth) return 1;
      return Math.max(1, Math.round(this.track.scrollWidth / this.track.clientWidth));
    }

    buildDots() {
      if (!this.dotsWrap) return;
      var pages = this.pageCount();
      this.dotsWrap.innerHTML = '';
      if (pages <= 1) return;

      for (var i = 0; i < pages; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'aw-ugcvl__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Slide ' + (i + 1));
        if (i === 0) dot.classList.add('is-active');
        dot.addEventListener(
          'click',
          ((index) => () => {
            this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
          })(i)
        );
        this.dotsWrap.appendChild(dot);
      }
    }

    updateActiveDot() {
      if (!this.dotsWrap || !this.track.clientWidth) return;
      var dots = this.dotsWrap.children;
      if (!dots.length) return;
      var activeIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === activeIndex);
      }
    }

    onDragStart(event) {
      if (event.pointerType === 'touch') return;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.dragging = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);

      var onMove = (moveEvent) => {
        if (!this.dragging) return;
        this.track.scrollLeft = this.dragStartScroll - (moveEvent.clientX - this.dragStartX);
      };
      var onEnd = () => {
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        this.track.removeEventListener('pointermove', onMove);
        this.track.removeEventListener('pointerup', onEnd);
        this.track.removeEventListener('pointercancel', onEnd);
      };

      this.track.addEventListener('pointermove', onMove);
      this.track.addEventListener('pointerup', onEnd);
      this.track.addEventListener('pointercancel', onEnd);
    }

    setupReveal() {
      if (this.dataset.reveal !== 'true' || REDUCED_MOTION.matches || !('IntersectionObserver' in window)) {
        return;
      }
      var observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.2 }
      );
      this.items.forEach((item) => observer.observe(item));
      this.revealObserver = observer;
    }
  }

  customElements.define('aw-ugc-video-list', AwUgcVideoList);
})();
